(() => {
  "use strict";
  const PAGE_SIZE = 48;
  const collator = new Intl.Collator(["en", "ja"], { numeric: true, sensitivity: "base" });
  const escapeHtml = (value) => String(value ?? "").replace(/[&<>'"]/g, (character) => ({"&":"&amp;","<":"&lt;",">":"&gt;","'":"&#39;",'"':"&quot;"}[character]));
  const money = (value, currencyCode = window.CroutonStorefrontContext?.currency || "JPY", locale = window.CroutonStorefrontContext?.locale || document.documentElement.lang || navigator.language || "ja-JP") => { const amount = Number(value); if (!Number.isFinite(amount)) return "価格未設定"; try { return new Intl.NumberFormat(locale, { style: "currency", currency: currencyCode }).format(amount); } catch (_) { return new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }).format(amount); } };
  const parsePrice = (value) => Number(String(value || "0").replace(/[^0-9.-]/g, "")) || 0;
  const titleGroup = (value) => {
    const c = String(value || "").normalize("NFKC").trim().charAt(0);
    if (/^[A-Za-z]$/.test(c)) return 0;
    if (/^[0-9]$/.test(c)) return 1;
    if (/^[ぁ-んァ-ヶ一-龠々]$/.test(c)) return 2;
    return 3;
  };
  const compareNames = (a, b) => {
    const ag = titleGroup(a.dataset.productTitle || a.querySelector(".new-card-info strong")?.textContent);
    const bg = titleGroup(b.dataset.productTitle || b.querySelector(".new-card-info strong")?.textContent);
    if (ag !== bg) return ag - bg;
    return collator.compare(
      String(a.dataset.productTitle || a.querySelector(".new-card-info strong")?.textContent || "").normalize("NFKC"),
      String(b.dataset.productTitle || b.querySelector(".new-card-info strong")?.textContent || "").normalize("NFKC")
    );
  };
  const paginationItems = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, null, total];
    if (current >= total - 3) return [1, null, total - 4, total - 3, total - 2, total - 1, total];
    return [1, null, current - 1, current, current + 1, null, total];
  };

  function initGridScope(scope) {
    if (scope.dataset.v121Bound === "true") return;
    const grid = scope.querySelector("[data-v121-product-grid]");
    if (!grid) return;
    scope.dataset.v121Bound = "true";
    const search = scope.querySelector(".vanguard-detail-search");
    const input = search?.querySelector("input[type=search]");
    const clear = search?.querySelector("button");
    const count = search?.querySelector("p b") || scope.querySelector("[data-v125-result-count]");
    const empty = scope.querySelector("[data-search-empty]");
    const sort = scope.querySelector("[data-v121-sort]");
    const stockToggle = scope.querySelector("[data-v125-stock-toggle]");
    const remoteToggle = scope.querySelector("[data-v127-remote-toggle]");
    const filterStatus = scope.querySelector("[data-v125-filter-status]");
    const pagination = scope.querySelector("[data-v121-client-pagination]");
    const pageSize = Math.max(1, Number(grid.dataset.v121PageSize || PAGE_SIZE));
    const productTypeIncludes = String(grid.dataset.productTypeIncludes || "").trim();
    const totalItemsValue = () => Math.max(0, Number(grid.dataset.v122TotalItems || 0));
    const usesRemotePagination = grid.dataset.v122RemotePagination === "true" && (totalItemsValue() > 0 || Boolean(productTypeIncludes));
    const defaultOrder = grid.dataset.v127DefaultOrder || (grid.dataset.sort === "created-descending" ? "newest" : "name-asc");
    let currentPage = Math.max(1, Number(grid.dataset.currentPage || 1));
    let pageLoading = false;
    let pageLoadError = false;
    let rendering = false;
    let observer = null;
    let observerScheduled = false;
    let remoteFilterActive = false;
    let remoteTotalItems = 0;
    let remoteTotalPages = 0;
    let filterTimer = null;
    let filterController = null;
    const observeGrid = () => observer?.observe(grid, { childList: true });
    const stockOnly = () => stockToggle?.classList.contains("is-active") ?? false;
    const remoteOnly = () => remoteToggle?.classList.contains("is-active") ?? false;
    const queryValue = () => String(input?.value || "").trim().normalize("NFKC").toLocaleLowerCase("ja");
    const activeOrder = () => sort?.value || grid.dataset.v127CurrentOrder || defaultOrder;
    const remoteFilterRequested = () => usesRemotePagination && (Boolean(queryValue()) || stockOnly() || remoteOnly());

    const setFilterStatus = (state) => {
      if (!filterStatus) return;
      filterStatus.hidden = state === "hidden";
      filterStatus.classList.toggle("is-error", state === "error");
      const title = filterStatus.querySelector("b");
      const text = filterStatus.querySelector("p");
      if (state === "loading") {
        if (title) title.textContent = "コレクション全商品を絞り込んでいます";
        if (text) text.textContent = "商品の取得までしばらくお待ちください。";
      } else if (state === "error") {
        if (title) title.textContent = "商品を取得できませんでした";
        if (text) text.textContent = "時間をおいて、もう一度お試しください。";
      }
    };
    const prepare = () => {
      grid.querySelectorAll(":scope > li").forEach((li, index) => {
        if (!li.dataset.productTitle) li.dataset.productTitle = (li.querySelector(".new-card-info strong")?.textContent || "").trim();
        if (!li.dataset.productPrice) li.dataset.productPrice = String(parsePrice(li.querySelector(".new-card-price b")?.textContent));
        if (!li.dataset.productAvailable) li.dataset.productAvailable = li.querySelector(".new-card-item")?.classList.contains("is-sold-out") ? "false" : "true";
        if (!li.dataset.productRemote) { const title = li.dataset.productTitle || ""; li.dataset.productRemote = title.includes("別拠点在庫") || title.includes("倉庫在庫") ? "true" : "false"; }
        if (!li.dataset.productSourceIndex) li.dataset.productSourceIndex = String(index);
      });
    };
    const pageCountFor = (filteredLength) => {
      if (remoteFilterActive) return Math.max(1, remoteTotalPages || 1);
      return Math.max(1, Math.ceil((usesRemotePagination ? totalItemsValue() : filteredLength) / pageSize));
    };
    const renderPagination = (totalPages) => {
      if (!pagination) return;
      pagination.innerHTML = "";
      pagination.setAttribute("aria-busy", pageLoading ? "true" : "false");
      if (totalPages > 1) {
        const pages = document.createElement("div");
        pages.className = "product-grid-pagination-pages";
        const add = (parent, label, page, className, disabled = false, current = false, useHtml = false) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = className;
          if (useHtml) button.innerHTML = label; else button.textContent = label;
          button.disabled = disabled || pageLoading;
          if (current) button.setAttribute("aria-current", "page");
          if (!button.disabled && !current) button.addEventListener("click", () => changePage(page));
          parent.appendChild(button);
        };
        add(pagination, '<span aria-hidden="true">←</span><b>前のページ</b>', currentPage - 1, "product-grid-pagination-direction previous", currentPage <= 1, false, true);
        paginationItems(currentPage, totalPages).forEach((item) => {
          if (item === null) {
            const span = document.createElement("span");
            span.className = "product-grid-pagination-ellipsis";
            span.textContent = "…";
            pages.appendChild(span);
          } else {
            add(pages, String(item), item, "product-grid-pagination-page" + (item === currentPage ? " is-current" : ""), false, item === currentPage);
          }
        });
        pagination.appendChild(pages);
        add(pagination, '<b>次のページ</b><span aria-hidden="true">→</span>', currentPage + 1, "product-grid-pagination-direction next", currentPage >= totalPages, false, true);
      }
      if (pageLoadError) {
        const error = document.createElement("p");
        error.className = "product-grid-pagination-error";
        error.setAttribute("role", "alert");
        error.textContent = "商品の読み込みに失敗しました。もう一度お試しください。";
        pagination.appendChild(error);
      }
    };
    const apply = (scroll = false) => {
      if (rendering) return;
      rendering = true;
      prepare();
      const query = queryValue();
      const mode = activeOrder();
      let items = Array.from(grid.querySelectorAll(":scope > li"));
      items.sort((a, b) => {
        if (mode === "newest") {
          const dateValue=(li)=>{const raw=String(li.dataset.productCreated||"");const numeric=Number(raw);if(Number.isFinite(numeric)&&numeric>0)return numeric<100000000000?numeric*1000:numeric;const parsed=Date.parse(raw);return Number.isFinite(parsed)?parsed:0};
          const dateDiff=dateValue(b)-dateValue(a);
          return dateDiff||Number(a.dataset.productSourceIndex||0)-Number(b.dataset.productSourceIndex||0);
        }
        if (mode === "name-asc") return compareNames(a, b);
        if (mode === "name-desc") return -compareNames(a, b);
        const diff = parsePrice(a.dataset.productPrice) - parsePrice(b.dataset.productPrice);
        return diff === 0 ? compareNames(a, b) : (mode === "price-asc" ? diff : -diff);
      });
      const orderChanged = items.some((li, index) => grid.children[index] !== li);
      if (orderChanged) {
        observer?.disconnect();
        const fragment = document.createDocumentFragment();
        items.forEach((li) => fragment.appendChild(li));
        grid.appendChild(fragment);
        observeGrid();
      }
      let filtered = items;
      if (!remoteFilterActive && query) filtered = filtered.filter((li) => String(li.dataset.productTitle || "").normalize("NFKC").toLocaleLowerCase("ja").includes(query));
      if (!remoteFilterActive && stockOnly()) filtered = filtered.filter((li) => li.dataset.productAvailable === "true");
      if (!remoteFilterActive && remoteOnly()) filtered = filtered.filter((li) => li.dataset.productRemote === "true");
      const totalPages = pageCountFor(filtered.length);
      currentPage = Math.min(currentPage, totalPages);
      items.forEach((li) => { li.hidden = true; });
      const visible = usesRemotePagination ? filtered : filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
      visible.forEach((li) => { li.hidden = false; });
      const filteredCount = remoteFilterActive ? remoteTotalItems : ((query || stockOnly() || remoteOnly()) ? filtered.length : (usesRemotePagination ? totalItemsValue() : filtered.length));
      if (count) count.textContent = Number(filteredCount).toLocaleString("ja-JP");
      if (clear) clear.hidden = !input?.value;
      const feedReady = grid.dataset.feedLoaded === "true" || items.length > 0;
      if (empty) empty.hidden = !feedReady || filtered.length !== 0;
      if (!pageLoading) grid.hidden = feedReady && filtered.length === 0;
      renderPagination(filteredCount ? totalPages : 0);
      rendering = false;
      if (scroll) scope.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
    };
    const loadBasePage = async (page = 1, scroll = false) => {
      const loader = window.CroutonProductFeed?.loadPage;
      if (typeof loader !== "function") return;
      filterController?.abort();
      filterController = new AbortController();
      pageLoading = true;
      pageLoadError = false;
      setFilterStatus("loading");
      grid.hidden = true;
      observer?.disconnect();
      try {
        await loader(grid, page, { order: activeOrder(), signal: filterController.signal });
        remoteFilterActive = false;
        remoteTotalItems = 0;
        remoteTotalPages = 0;
        currentPage = page;
        pageLoading = false;
        setFilterStatus("hidden");
        grid.hidden = false;
        observeGrid();
        apply(scroll);
      } catch (error) {
        if (error?.name === "AbortError") return;
        pageLoading = false;
        pageLoadError = true;
        setFilterStatus("error");
        observeGrid();
        renderPagination(pageCountFor(grid.querySelectorAll(":scope > li").length));
      }
    };
    const loadRemoteFilter = async (page = 1, scroll = false) => {
      const loader = window.CroutonProductFeed?.filterCollection;
      if (typeof loader !== "function") { pageLoadError = true; setFilterStatus("error"); return; }
      filterController?.abort();
      filterController = new AbortController();
      pageLoading = true;
      pageLoadError = false;
      remoteFilterActive = true;
      setFilterStatus("loading");
      grid.hidden = true;
      observer?.disconnect();
      try {
        const result = await loader(grid, { page, pageSize, query: queryValue(), availableOnly: stockOnly(), remoteOnly: remoteOnly(), order: activeOrder(), signal: filterController.signal });
        remoteTotalItems = Number(result.totalItems || 0);
        remoteTotalPages = Number(result.totalPages || 0);
        currentPage = Number(result.page || 1);
        pageLoading = false;
        setFilterStatus("hidden");
        grid.hidden = false;
        observeGrid();
        apply(scroll);
      } catch (error) {
        if (error?.name === "AbortError") return;
        pageLoading = false;
        pageLoadError = true;
        setFilterStatus("error");
        observeGrid();
        renderPagination(Math.max(1, remoteTotalPages || 1));
      }
    };
    const scheduleFilter = () => {
      window.clearTimeout(filterTimer);
      filterController?.abort();
      currentPage = 1;
      pageLoadError = false;
      if (!usesRemotePagination) { remoteFilterActive = false; apply(); return; }
      if (!remoteFilterRequested()) { loadBasePage(1); return; }
      remoteFilterActive = true;
      pageLoading = true;
      setFilterStatus("loading");
      grid.hidden = true;
      filterTimer = window.setTimeout(() => loadRemoteFilter(1), 450);
    };
    const changePage = async (nextPage) => {
      const totalPages = remoteFilterActive ? Math.max(1, remoteTotalPages || 1) : pageCountFor(grid.querySelectorAll(":scope > li").length);
      const safePage = Math.min(totalPages, Math.max(1, Number(nextPage) || 1));
      if (safePage === currentPage || pageLoading) return;
      if (remoteFilterActive) { await loadRemoteFilter(safePage, true); return; }
      if (!usesRemotePagination) { currentPage = safePage; apply(true); return; }
      await loadBasePage(safePage, true);
      if (input) input.value = "";
    };
    input?.addEventListener("input", scheduleFilter);
    clear?.addEventListener("click", () => { if (input) input.value = ""; scheduleFilter(); input?.focus(); });
    sort?.addEventListener("change", () => { currentPage = 1; grid.dataset.v127CurrentOrder = sort.value; if (usesRemotePagination) { if (remoteFilterRequested()) scheduleFilter(); else loadBasePage(1); } else apply(); });
    stockToggle?.addEventListener("click", () => { const active = !stockToggle.classList.contains("is-active"); stockToggle.classList.toggle("is-active", active); stockToggle.setAttribute("aria-pressed", active ? "true" : "false"); scheduleFilter(); });
    remoteToggle?.addEventListener("click", () => { const active = !remoteToggle.classList.contains("is-active"); remoteToggle.classList.toggle("is-active", active); remoteToggle.setAttribute("aria-pressed", active ? "true" : "false"); scheduleFilter(); });
    grid.addEventListener("crouton:localized-prices", () => { if (!pageLoading) apply(); });
    observer = new MutationObserver((mutations) => {
      const hasChildChanges = mutations.some((mutation) => mutation.type === "childList" && (mutation.addedNodes.length || mutation.removedNodes.length));
      if (rendering || observerScheduled || pageLoading || !hasChildChanges) return;
      observerScheduled = true;
      if (!usesRemotePagination) currentPage = 1;
      queueMicrotask(() => { observerScheduled = false; apply(); });
    });
    observeGrid();
    grid.dataset.v127CurrentOrder = activeOrder();
    apply();
  }

  function initSearchTitleSelector() {
    const page = document.querySelector("[data-v121-search-page]");
    const form = page?.querySelector("[data-v121-search-form]");
    if (!page || !form) return;
    const cards = Array.from(page.querySelectorAll("[data-search-game-card]"));
    const queryDisplay = form.querySelector("[data-search-query-display]");
    const queryValue = form.querySelector("[data-search-query-value]");
    const gameValue = form.querySelector("[data-search-game-value]");
    const requested = new URLSearchParams(location.search).get("game");
    let selected = cards.some((card) => card.dataset.searchGameCard === requested) ? requested : (cards.find((c) => c.classList.contains("is-active"))?.dataset.searchGameCard || "vanguard");
    const applySelected = () => {
      cards.forEach((card) => {
        const active = card.dataset.searchGameCard === selected;
        card.classList.toggle("is-active", active);
        const label = card.querySelector(".search-title-filter-copy b");
        if (label) label.textContent = active ? "検索対象に選択中" : "このタイトルで検索";
      });
      if (gameValue) gameValue.value = selected;
    };
    cards.forEach((card) => card.querySelector("[data-search-title-select]")?.addEventListener("click", (event) => {
      event.preventDefault(); selected = card.dataset.searchGameCard || "vanguard"; applySelected(); queryDisplay?.focus();
    }));
    form.addEventListener("submit", () => {
      const card = cards.find((c) => c.dataset.searchGameCard === selected) || cards[0];
      const raw = String(queryDisplay?.value || "").trim();
      if (queryValue) queryValue.value = raw ? `${raw} AND product_type:"${card?.dataset.productType || ""}"` : `product_type:"${card?.dataset.productType || ""}"`;
      if (gameValue) gameValue.value = selected;
    });
    applySelected();
  }

  let quickLayer = null;
  let quickStockController = null;
  function closeQuickCart() {
    quickStockController?.abort();
    quickStockController = null;
    quickLayer?.remove();
    quickLayer = null;
    document.body.classList.remove("lock");
  }
  function productHandleFromForm(form) {
    const direct = String(form.dataset.productHandle || "").trim();
    if (/^[a-z0-9][a-z0-9_-]*$/i.test(direct)) return direct;
    try {
      const url = new URL(form.dataset.productUrl || "", location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      const handle = match ? decodeURIComponent(match[1]) : "";
      return /^[a-z0-9][a-z0-9_-]*$/i.test(handle) ? handle : "";
    } catch (_) {
      return "";
    }
  }
  async function fetchQuickStock(handle, signal, fallbackStock = null) {
    if (!/^[a-z0-9][a-z0-9_-]*$/i.test(handle)) throw new Error("product handle");
    const context = window.CroutonStorefrontContext || {};
    const endpoint = context.shopDomain && context.apiVersion
      ? `https://${context.shopDomain}/api/${context.apiVersion}/graphql.json`
      : "";
    if (!endpoint || !context.accessToken) throw new Error("storefront context");
    const response = await fetch(endpoint, {
      method: "POST",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": context.accessToken,
      },
      body: JSON.stringify({
        query: `query ProductAvailability($handle: String!) { product(handle: $handle) { variants(first: 1) { nodes { id availableForSale } } } }`,
        variables: { handle },
      }),
      signal,
    });
    if (!response.ok) throw new Error("product inventory");
    const payload = await response.json();
    if (payload?.errors?.length) throw new Error("product inventory");
    const variant = payload?.data?.product?.variants?.nodes?.[0];
    if (!variant) throw new Error("product variant");
    const variantIdValue = Number(String(variant.id || "").match(/\/ProductVariant\/(\d+)$/)?.[1]);
    const variantId = Number.isSafeInteger(variantIdValue) && variantIdValue > 0 ? variantIdValue : null;
    const fallbackMatches = Number(fallbackStock?.variantId) === variantId;
    const fallbackInventory = Number(fallbackStock?.inventoryQuantity);
    const inventoryQuantity = fallbackMatches && variant.availableForSale && Number.isFinite(fallbackInventory) && fallbackInventory >= 0
      ? Math.floor(fallbackInventory)
      : null;
    const maximum = inventoryQuantity !== null && inventoryQuantity > 0
      ? inventoryQuantity
      : fallbackMatches && variant.availableForSale && Number(fallbackStock?.maximum) > 0
        ? Math.floor(Number(fallbackStock.maximum))
      : variant.availableForSale
        ? 99
        : 0;
    const available = Boolean(variant.availableForSale && variantId && maximum > 0);
    return { variantId, available, inventoryQuantity, maximum };
  }
  function stockFallbackFromForm(form) {
    const rawVariantId = Number(form.querySelector('input[name="id"]')?.value);
    const variantId = Number.isSafeInteger(rawVariantId) && rawVariantId > 0 ? rawVariantId : null;
    const quantity = form.querySelector('input[name="quantity"]');
    const rawMaximum = Number(quantity?.max);
    const available = Boolean(variantId && form.dataset.canPurchase === "true");
    const maximum = available && Number.isFinite(rawMaximum) && rawMaximum > 0 ? Math.floor(rawMaximum) : available ? 99 : 0;
    const label = form.querySelector(".product-stock-row div strong")?.textContent || "";
    const match = label.normalize("NFKC").match(/残り\s*([0-9,]+)\s*個/);
    const parsedInventory = match ? Number(match[1].replace(/,/g, "")) : NaN;
    const inventoryQuantity = Number.isFinite(parsedInventory) && parsedInventory >= 0 ? Math.floor(parsedInventory) : null;
    return { variantId, available, inventoryQuantity, maximum };
  }
  function stockFallbackFromQuickForm(form) {
    const rawVariantId = Number(form.querySelector('input[name="id"]')?.value);
    const variantId = Number.isSafeInteger(rawVariantId) && rawVariantId > 0 ? rawVariantId : null;
    const button = form.querySelector('button[type="submit"]');
    const available = Boolean(variantId && !button?.disabled);
    return { variantId, available, inventoryQuantity: null, maximum: available ? 99 : 0 };
  }
  async function currentCartQuantity(variantId) {
    try {
      const response = await fetch("/cart.js", {
        cache: "no-store",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return 0;
      const cart = await response.json();
      const item = Array.isArray(cart?.items)
        ? cart.items.find((entry) => Number(entry.variant_id ?? entry.id) === Number(variantId))
        : null;
      return Math.max(0, Number(item?.quantity || 0));
    } catch (_) {
      return 0;
    }
  }
  function renderQuickStock(layer, stock, state = "ready", reservationMode = false) {
    const stockNode = layer.querySelector(".quick-cart-stock");
    const quantity = layer.querySelector("#quick-cart-quantity");
    const addButton = layer.querySelector(".quick-cart-add");
    const maximum = Math.max(0, Number(stock?.maximum ?? stock?.inventoryQuantity ?? 0));
    const available = Boolean(stock?.available && stock?.variantId && maximum > 0);
    if (stockNode) {
      stockNode.classList.toggle("is-available", available);
      stockNode.classList.toggle("is-unavailable", !available);
      const label =
        state === "loading"
          ? "現在庫を確認中"
          : state === "error"
            ? "現在庫を確認できません"
            : stock?.inventoryQuantity !== null && stock?.inventoryQuantity !== undefined && stock.inventoryQuantity > 0
              ? `残り${stock.inventoryQuantity}個`
              : available
                ? reservationMode
                  ? "予約受付中"
                  : "購入可能"
                : "在庫切れ";
      stockNode.innerHTML = `<i></i>${label}`;
    }
    if (quantity) {
      const current = Math.max(1, Number(quantity.value || 1));
      quantity.innerHTML = Array.from(
        { length: Math.max(1, maximum) },
        (_, index) => `<option value="${index + 1}">${index + 1}</option>`,
      ).join("");
      quantity.value = String(Math.min(Math.max(1, maximum), current));
      quantity.disabled = !available || state !== "ready";
    }
    if (addButton) {
      addButton.disabled = !available || state !== "ready";
      if (state === "loading") addButton.textContent = "現在庫を確認中";
      else if (state === "error") addButton.textContent = "現在庫を確認できません";
      else addButton.textContent = "カートに入れる";
    }
    layer.dataset.quickVariantId = stock?.variantId ? String(stock.variantId) : "";
    layer.dataset.quickMaximum = String(maximum);
  }
  async function openQuickCart(form) {
    const initialVariantId = form.querySelector('input[name="id"]')?.value;
    if (!initialVariantId) return;
    const fallbackStock = stockFallbackFromQuickForm(form);
    const handle = productHandleFromForm(form);
    if (!handle) return;
    const title = form.dataset.productTitle || "商品";
    const price = parsePrice(form.dataset.productPrice);
    const currency = form.dataset.productCurrency || window.CroutonStorefrontContext?.currency || "JPY";
    const locale = window.CroutonStorefrontContext?.locale || document.documentElement.lang || navigator.language || "ja-JP";
    const formattedPrice = form.dataset.productPriceFormatted || money(price, currency, locale);
    const image = form.dataset.productImage || "";
    const url = form.dataset.productUrl || "#";
    const reservationMode = form.dataset.reservationMode === "true";
    quickStockController?.abort();
    quickStockController = new AbortController();
    quickLayer = document.createElement("div");
    quickLayer.className = "quick-cart-layer";
    const remoteBadge=form.dataset.productRemote==="true"||title.includes("別拠点在庫")||title.includes("倉庫在庫")?`<span class="quick-cart-location">別拠点在庫</span>`:"";
    quickLayer.innerHTML = `<button class="quick-cart-backdrop" type="button" aria-label="閉じる"></button><section class="quick-cart-dialog" role="dialog" aria-modal="true" aria-label="クイックカート"><button class="quick-cart-close" type="button" aria-label="閉じる">×</button><header class="quick-cart-header"><span>QUICK CART</span><strong>カートへ追加</strong></header><div data-quick-cart-body><div class="quick-cart-product"><span class="quick-cart-image">${image ? `<img src="${image}" alt="">` : `<b>NO IMAGE</b>`}</span><div><h2></h2><p>販売価格<strong>${escapeHtml(formattedPrice)}</strong><small>（税込）</small></p><span class="quick-cart-stock is-unavailable" aria-live="polite"><i></i>現在庫を確認中</span>${remoteBadge}</div></div><div class="quick-cart-controls"><label for="quick-cart-quantity">数量</label><select id="quick-cart-quantity" disabled><option value="1">1</option></select><a href="${url}">商品詳細を見る</a></div><button class="quick-cart-add" type="button" disabled>現在庫を確認中</button></div></section>`;
    quickLayer.querySelector("h2").textContent = title;
    quickLayer.querySelectorAll(".quick-cart-backdrop,.quick-cart-close").forEach((node) => node.addEventListener("click", closeQuickCart));
    const layer = quickLayer;
    layer.querySelector(".quick-cart-add").addEventListener("click", async (event) => {
      const button = event.currentTarget;
      if (button.disabled) return;
      const controller = new AbortController();
      quickStockController?.abort();
      quickStockController = controller;
      renderQuickStock(layer, null, "loading", reservationMode);
      try {
        const stock = await fetchQuickStock(handle, controller.signal, fallbackStock);
        if (layer !== quickLayer) return;
        renderQuickStock(layer, stock, "ready", reservationMode);
        const maximum = Math.max(0, Number(stock.maximum ?? stock.inventoryQuantity ?? 0));
        if (!stock.available || !stock.variantId || maximum < 1) return;
        const existing = await currentCartQuantity(stock.variantId);
        if (layer !== quickLayer) return;
        const addable = Math.max(0, maximum - existing);
        if (addable < 1) {
          const stockNode = layer.querySelector(".quick-cart-stock");
          if (stockNode) {
            stockNode.classList.remove("is-available");
            stockNode.classList.add("is-unavailable");
            stockNode.innerHTML = "<i></i>カート内の商品で在庫上限です";
          }
          button.disabled = true;
          return;
        }
        const requested = Math.max(1, Number(layer.querySelector("select")?.value || 1));
        const quantity = Math.min(addable, requested);
        button.disabled = true;
        button.textContent = "現在庫を再確認中";
        const response = await fetch("/cart/add.js", {
          method: "POST",
          cache: "no-store",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: Number(stock.variantId), quantity }] }),
        });
        if (!response.ok) throw new Error("cart");
        const body = layer.querySelector("[data-quick-cart-body]");
        body.innerHTML = `<div class="quick-cart-success" aria-live="polite"><span>✓</span><small>ADDED TO CART</small><h2>カートに追加しました</h2><p></p><div><button type="button">買い物を続ける</button><a href="/cart">カートを見る</a></div></div>`;
        body.querySelector("p").textContent = `${title}を${quantity}点追加しました。`;
        body.querySelector("button").addEventListener("click", closeQuickCart);
      } catch (error) {
        if (error?.name === "AbortError") return;
        if (layer === quickLayer) renderQuickStock(layer, null, "error", reservationMode);
      }
    });
    document.body.appendChild(quickLayer);
    document.body.classList.add("lock");
    quickLayer.querySelector(".quick-cart-close")?.focus();
    try {
      const stock = await fetchQuickStock(handle, quickStockController.signal, fallbackStock);
      if (layer === quickLayer) renderQuickStock(layer, stock, "ready", reservationMode);
    } catch (error) {
      if (error?.name !== "AbortError" && layer === quickLayer) renderQuickStock(layer, null, "error", reservationMode);
    }
  }
  function initQuickCart() {
    document.addEventListener("submit", (event) => {
      const form = event.target.closest?.("[data-v121-quick-cart]");
      if (!form) return;
      event.preventDefault();
      void openQuickCart(form);
    });
    document.addEventListener("keydown", (event) => { if (event.key === "Escape" && quickLayer) closeQuickCart(); });
  }

  function renderProductStock(form, stock, state = "ready") {
    const stockRow = form.querySelector(".product-stock-row");
    const dot = stockRow?.querySelector(":scope > span");
    const label = stockRow?.querySelector("div strong");
    const status = stockRow?.querySelector(":scope > b");
    const quantity = form.querySelector('input[name="quantity"]');
    const button = form.querySelector(".product-add-cart");
    const strong = button?.querySelector("strong");
    const small = button?.querySelector("small");
    const maximum = Math.max(0, Number(stock?.maximum ?? stock?.inventoryQuantity ?? 0));
    const available = Boolean(stock?.available && stock?.variantId && maximum > 0 && state === "ready");
    const reservationMode = form.dataset.reservationMode === "true";
    form.dataset.canPurchase = available ? "true" : "false";
    if (stock?.variantId) {
      let idInput = form.querySelector('input[name="id"]');
      if (!idInput) { idInput = document.createElement("input"); idInput.type = "hidden"; idInput.name = "id"; form.prepend(idInput); }
      idInput.value = String(stock.variantId);
    }
    if (dot) { dot.classList.toggle("is-available", available); dot.classList.toggle("is-unavailable", !available); }
    if (label) label.textContent = state === "loading" ? "現在庫を確認中" : state === "error" ? "現在庫を確認できません" : stock?.inventoryQuantity !== null && stock?.inventoryQuantity !== undefined && stock.inventoryQuantity > 0 ? `残り${stock.inventoryQuantity}個` : available ? (reservationMode ? "予約受付中" : "購入可能") : "在庫切れ";
    if (status) status.textContent = available ? (reservationMode ? "予約受付中" : "購入可能") : state === "loading" ? "確認中" : "販売停止中";
    if (quantity) { quantity.max = String(Math.max(1, maximum)); quantity.value = String(maximum > 0 ? Math.min(maximum, Math.max(1, Number(quantity.value || 1))) : 1); quantity.disabled = !available; }
    if (button) button.disabled = !available;
    if (strong) strong.textContent = state === "adding" ? "現在庫を再確認中" : "カートに入れる";
    if (small) small.textContent = available ? "ADD TO CART" : state === "loading" ? "CHECKING" : "SOLD OUT";
    form.dispatchEvent(new CustomEvent("crouton:stock-updated"));
    return { available, maximum };
  }
  async function initProductFormStock(form) {
    const handle = productHandleFromForm(form);
    if (!handle) return;
    const fallbackStock = stockFallbackFromForm(form);
    renderProductStock(form, fallbackStock, "loading");
    try { const stock = await fetchQuickStock(handle, undefined, fallbackStock); renderProductStock(form, stock, "ready"); }
    catch (_) { renderProductStock(form, null, "error"); }
  }
  function initProductAjaxCart() {
    document.querySelectorAll("[data-v121-product-form]").forEach((form) => {
      if (form.dataset.ajaxBound === "true") return;
      form.dataset.ajaxBound = "true";
      void initProductFormStock(form);
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        const button = form.querySelector(".product-add-cart");
        const success = form.querySelector("[data-product-cart-success]");
        const handle = productHandleFromForm(form);
        if (!button || button.disabled || !handle) return;
        button.disabled = true;
        const strong = button.querySelector("strong");
        if (strong) strong.textContent = "現在庫を再確認中";
        try {
          const stock = await fetchQuickStock(handle, undefined, stockFallbackFromForm(form));
          const state = renderProductStock(form, stock, "ready");
          if (!state.available || !stock.variantId || state.maximum < 1) return;
          const existing = await currentCartQuantity(stock.variantId);
          const addable = Math.max(0, state.maximum - existing);
          if (addable < 1) {
            const label = form.querySelector(".product-stock-row div strong");
            if (label) label.textContent = "カート内の商品で在庫上限です";
            form.dataset.canPurchase = "false";
            button.disabled = true;
            return;
          }
          const quantityInput = form.querySelector('input[name="quantity"]');
          const requested = Math.max(1, Number(quantityInput?.value || 1));
          const quantity = Math.min(addable, requested);
          if (quantityInput) quantityInput.value = String(quantity);
          const response = await fetch("/cart/add.js", { method: "POST", cache: "no-store", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items: [{ id: Number(stock.variantId), quantity }] }) });
          if (!response.ok) throw new Error("cart");
          if (success) { success.hidden = false; const message = success.querySelector("strong"); if (message) message.textContent = `カートに${quantity}点追加しました。`; }
          const remainingInventory = stock.inventoryQuantity !== null && stock.inventoryQuantity !== undefined ? Math.max(0, Number(stock.inventoryQuantity) - quantity) : null;
          renderProductStock(form, { ...stock, inventoryQuantity: remainingInventory, maximum: Math.max(0, state.maximum - quantity) }, "ready");
        } catch (_) {
          renderProductStock(form, null, "error");
          if (success) { success.hidden = false; const message = success.querySelector("strong"); if (message) message.textContent = "カートに追加できませんでした。"; }
        }
      });
    });
  }

  function initCartControls() {
    const form = document.querySelector("[data-v121-cart-form]");
    if (!form) return;
    const change = async (line, quantity) => {
      const response = await fetch("/cart/change.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ line, quantity }) });
      if (response.ok) location.reload();
    };
    form.querySelectorAll("[data-cart-line]").forEach((row) => {
      const line = Number(row.dataset.cartLine); const input = row.querySelector("[data-cart-quantity]");
      row.querySelector("[data-cart-minus]")?.addEventListener("click", () => change(line, Math.max(1, Number(input.value || 1) - 1)));
      row.querySelector("[data-cart-plus]")?.addEventListener("click", () => change(line, Number(input.value || 1) + 1));
      input?.addEventListener("change", () => change(line, Math.max(1, Number(input.value || 1))));
      row.querySelector("[data-cart-remove]")?.addEventListener("click", (event) => { event.preventDefault(); change(line, 0); });
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("[data-v121-grid-scope]").forEach(initGridScope);
    initSearchTitleSelector();
    initQuickCart();
    initProductAjaxCart();
    initCartControls();
  });
})();
